import dayjs from 'dayjs'
import { useIsFocused, useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@/src/elements/button/default'
import { Streak } from '@/src/elements/streak/Streak'
import { useStreakWatchmanQuery } from '@/src/features/watchman/services/useStreakWatchmanQuery'
import { useWatchmanDailyIntentionsQuery } from '@/src/features/watchman/services/useWatchmanDailyIntentionsQuery'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'
import { useRoutes } from '@/src/general/utils/routes'

import { applyTodayPrayerProgressToStreakDays } from './applyTodayPrayerProgressToStreakDays'

export type TWatchamanThumbnailProps = {
  totalPrayers: number
  answeredPrayers: number
}

export const WatchmanThumbnailBase = ({ totalPrayers, answeredPrayers }: TWatchamanThumbnailProps): ReactNode => {
  if (answeredPrayers > totalPrayers) throw new Error('answeredPrayers cannot be greater than totalPrayers')

  const today = dayjs(new Date()).format('YYYY-MM-DD')
  // The screen stays mounted in background (e.g. while praying on the intentions screen):
  // only allow the animation once the screen is focused, so the user can actually see it.
  const isFocused = useIsFocused()
  const hasAnimatedToday = !isFocused || storage.getItem(STORAGE_KEYS.WATCHMAN_STREAK_ANIMATED_TODAY) === today
  const { data: streakDays } = useStreakWatchmanQuery()

  const { navigate } = useRouter()
  const { routes } = useRoutes()

  const isInProgress = answeredPrayers > 0 && answeredPrayers < totalPrayers
  const isCompleted = totalPrayers === answeredPrayers

  if (!streakDays) return null

  const daysWithTodayProgress = applyTodayPrayerProgressToStreakDays({
    days: streakDays,
    today: dayjs(new Date()).format('YYYY-MM-DD'),
    answeredPrayers,
    totalPrayers,
  })

  let title: ReactNode
  if (isCompleted) {
    title = <FormattedMessage id="watchman.prayers.thank-you" />
  } else if (isInProgress) {
    title = <FormattedMessage id="watchman.rely.remains" values={{ count: totalPrayers - answeredPrayers }} />
  } else {
    title = <FormattedMessage id="watchman.daily-counted.intentions" values={{ count: totalPrayers }} />
  }

  const footer = isCompleted ? undefined : (
    <Button
      testID={TEST_ID.ELEMENT.WATCHMAN_THUMBNAIL}
      colors="secondary"
      variant="solid"
      onPress={() => navigate(routes.watchmanIntentions())}
      endIcon={ICON.CHEVRON_RIGHT}
      className="ml-auto mt-2"
    >
      <FormattedMessage id="watchman.pray-for-today" />
    </Button>
  )

  return (
    <Streak
      days={daysWithTodayProgress}
      today={today}
      hasAnimatedToday={hasAnimatedToday}
      title={title}
      footer={footer}
    />
  )
}

/**
 * @public
 */
export const WatchmanThumbnail = (): ReactNode => {
  const { data } = useWatchmanDailyIntentionsQuery()

  const totalPrayers = Array.isArray(data) ? data.length : 0
  const answeredPrayers = Array.isArray(data) ? data.filter((intention) => intention.status === 'prayed').length : 0

  if (!totalPrayers) return null

  return <WatchmanThumbnailBase totalPrayers={totalPrayers} answeredPrayers={answeredPrayers} />
}
